import { Role, Team, type AppConfiguration } from '@puffin/app-frame-api';
import { useState } from 'react';
import { createStore, useStore } from 'zustand';

export const APPLICATION_CONFIGURATION_STORAGE_KEY = 'mp.dev.platform.configuration';
const APPLICATION_CONFIGURATION_VERSION = 1;

interface StoredApplicationConfiguration {
  version: number;
  configuration: AppConfiguration;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isTeam = (value: unknown): value is Team => Object.values(Team).includes(value as Team);

const isRole = (value: unknown): value is Role => Object.values(Role).includes(value as Role);

export const loadAppConfiguration = (): AppConfiguration | undefined => {
  const storedValue = localStorage.getItem(APPLICATION_CONFIGURATION_STORAGE_KEY);
  if (!storedValue) {
    return undefined;
  }

  try {
    const storedConfiguration: unknown = JSON.parse(storedValue);
    if (
      !isRecord(storedConfiguration) ||
      storedConfiguration.version !== APPLICATION_CONFIGURATION_VERSION ||
      !isRecord(storedConfiguration.configuration) ||
      !isTeam(storedConfiguration.configuration.team) ||
      !isRole(storedConfiguration.configuration.role)
    ) {
      return undefined;
    }

    return {
      team: storedConfiguration.configuration.team,
      role: storedConfiguration.configuration.role,
    };
  } catch {
    return undefined;
  }
};

export const saveAppConfiguration = (configuration: AppConfiguration): void => {
  const storedConfiguration: StoredApplicationConfiguration = {
    version: APPLICATION_CONFIGURATION_VERSION,
    configuration,
  };
  localStorage.setItem(APPLICATION_CONFIGURATION_STORAGE_KEY, JSON.stringify(storedConfiguration));
};

interface ApplicationConfigurationState {
  configuration: AppConfiguration | undefined;
  saveConfiguration: (configuration: AppConfiguration) => void;
}

const createApplicationConfigurationStore = () =>
  createStore<ApplicationConfigurationState>((set) => ({
    configuration: loadAppConfiguration(),
    saveConfiguration: (configuration) => {
      saveAppConfiguration(configuration);
      set({ configuration });
    },
  }));

export const useApplicationConfiguration = () => {
  const [store] = useState(createApplicationConfigurationStore);
  return useStore(store);
};
